import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import type { DynamicListsApi } from './api'
import { accountName } from './hooks'
import { Modal } from './shared'
import type { AllGroup, CrmAccount } from './types'
import { BusyLabel } from '../components/Spinner'

const MAX_ADDS = 100
const fold = (s: string) => s.toLocaleLowerCase()

type Outcome = 'created' | 'skipped' | 'failed' | 'removed'
interface Result {
  account_id: string
  outcome: Outcome
  reason?: string
}

/**
 * Which Accounts have a rule (the All view): ticked = has it. Saving copies
 * the rule to newly ticked Accounts (copy: In list matched by name, never
 * adopts) and deletes the Dynamic list on unticked ones (their OnlyFans
 * lists stay). One outcome per Account.
 */
export function AccountsDialog({
  api,
  group,
  accounts,
  onClose,
  onChanged,
}: {
  api: DynamicListsApi
  group: AllGroup
  accounts: CrmAccount[]
  onClose: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const has = useMemo(() => new Map(group.accounts.map((l) => [l.account_id, l])), [group])
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(has.keys()))
  const [q, setQ] = useState('')
  const [enable, setEnable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState<Result[] | null>(null)

  const byId = useMemo(() => new Map(accounts.map((a) => [a.account_id, a])), [accounts])
  const label = (id: string) => {
    const a = byId.get(id)
    return a ? accountName(a) : id
  }
  // Accounts with the rule first, then the rest by name.
  const shown = useMemo(() => {
    const query = fold(q.trim())
    const rows = accounts.filter((a) => (a.platform || 'onlyfans') === 'onlyfans')
    for (const id of has.keys()) if (!byId.has(id)) rows.push({ account_id: id, profile_name: id, username: '', avatar_url: '', platform: 'onlyfans' } as CrmAccount)
    return rows
      .filter((a) => !query || [a.profile_name, a.username, a.account_id].some((s) => fold(s || '').includes(query)))
      .sort((a, b) => Number(has.has(b.account_id)) - Number(has.has(a.account_id)) || accountName(a).localeCompare(accountName(b), undefined, { sensitivity: 'base' }))
  }, [accounts, byId, has, q])

  const adds = [...ticked].filter((id) => !has.has(id))
  const removes = [...has.keys()].filter((id) => !ticked.has(id))
  const toggle = (id: string) =>
    setTicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else if (has.has(id) || adds.length < MAX_ADDS) next.add(id)
      return next
    })
  const allShown = shown.length > 0 && shown.every((a) => ticked.has(a.account_id))
  const toggleShown = () =>
    setTicked((s) => {
      const next = new Set(s)
      if (allShown) for (const a of shown) next.delete(a.account_id)
      else {
        let room = MAX_ADDS - adds.length
        for (const a of shown) {
          if (next.has(a.account_id)) continue
          if (!has.has(a.account_id) && room-- <= 0) continue
          next.add(a.account_id)
        }
      }
      return next
    })

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const out: Result[] = []
      // Copy first, from a list that stays, so removing every old Account still leaves the new ones.
      if (adds.length) {
        const source = group.accounts.find((l) => ticked.has(l.account_id)) ?? group.accounts[0]!
        const r = await api.copy(source.id, adds, enable)
        out.push(...r.results.map((x) => ({ account_id: x.account_id, outcome: x.outcome, reason: x.reason })))
      }
      const removed = await Promise.allSettled(removes.map((id) => api.remove(has.get(id)!.id)))
      removed.forEach((r, i) =>
        out.push({
          account_id: removes[i]!,
          outcome: r.status === 'fulfilled' ? 'removed' : 'failed',
          reason: r.status === 'rejected' ? (r.reason instanceof Error ? r.reason.message : String(r.reason)) : undefined,
        }),
      )
      setResults(out)
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const title = t('accounts.title', { name: group.name })

  if (results) {
    const count = (o: Outcome) => results.filter((r) => r.outcome === o).length
    return (
      <Modal
        title={title}
        onClose={onClose}
        wide
        footer={
          <button type="button" className="dl-btn dl-btn--primary" onClick={onClose}>
            {t('close')}
          </button>
        }
      >
        <p className="dl-strong">
          {t('accounts.summary', { created: count('created'), removed: count('removed'), skipped: count('skipped'), failed: count('failed') })}
        </p>
        <ul className="dl-results">
          {results.map((r) => (
            <li key={`${r.outcome}-${r.account_id}`} className="dl-result">
              <span className={`dl-outcome dl-outcome--${r.outcome}`}>{t(`accounts.outcome.${r.outcome}`)}</span>
              <span className="dl-ellipsis dl-strong">{label(r.account_id)}</span>
              {r.reason && <span className="dl-muted dl-ellipsis">{r.reason}</span>}
            </li>
          ))}
        </ul>
      </Modal>
    )
  }

  const changed = adds.length + removes.length > 0
  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          {adds.length > 0 && (
            <label className="dl-check">
              <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
              {t('accounts.enable')}
            </label>
          )}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={!changed || busy} onClick={() => void save()}>
            <BusyLabel busy={busy} label={t('saving')}>{t('save')}</BusyLabel>
          </button>
        </>
      }
    >
      <p className="dl-hint">{t('accounts.intro')}</p>
      <div className="dl-copy-bar">
        <label className="dl-search">
          <FeatherIcon name="search" size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('copy.search')} />
        </label>
        <span className="dl-muted">{t('accounts.changes', { adds: adds.length, removes: removes.length })}</span>
      </div>
      {removes.length > 0 && <p className="dl-warn">{t('accounts.removeWarn', { count: removes.length })}</p>}
      <div className="dl-checklist">
        {shown.length > 0 && (
          <label className="dl-check-row dl-check-row--all">
            <input type="checkbox" checked={allShown} onChange={toggleShown} />
            <span className="dl-strong">{t('copy.all')}</span>
          </label>
        )}
        {shown.length === 0 && <p className="dl-muted dl-pad">{t('account.none')}</p>}
        {shown.map((a) => {
          const on = ticked.has(a.account_id)
          const was = has.has(a.account_id)
          return (
            <label key={a.account_id} className={`dl-check-row ${on ? 'is-on' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(a.account_id)} />
              <span className="dl-ellipsis">{accountName(a)}</span>
              {a.username && <span className="dl-muted dl-ellipsis">@{a.username}</span>}
              {was && !on && <span className="dl-badge dl-badge--warn">{t('accounts.willRemove')}</span>}
              {!was && on && <span className="dl-badge">{t('accounts.willAdd')}</span>}
            </label>
          )
        })}
      </div>
    </Modal>
  )
}
